import { createSelector } from 'reselect'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import getFlightDetailsByOrderItems from 'lib/flights/getFlightDetailsByOrderItems'
import { getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import { getInsuranceDestinationCountriesFromOrder } from 'lib/order/orderInsuranceUtils'
import { canAddPassportCardInsuranceToOrder, getPassportCardInsurableOrderSlice } from 'lib/order/orderPassportCardInsuranceUtils'
import { isSpoofed } from 'selectors/isSpoofed'

/**
 * Whether PassportCard can be added post-purchase to `order` (or, when none is passed, to
 * the order behind the current post-purchase checkout). The My Escapes upsell and the
 * checkout insurance section both read this, so they make the same product decision.
 */
const isPassportCardInsurancePostPurchaseEnabled = createSelector(
  (state: App.State, order?: App.Order) => order ?? state.checkout.cart.existingOrder,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => isSpoofed(state),
  (state: App.State) => getOptimizelyFeatureFlagVariation(
    state,
    OptimizelyFeatureFlags.croPassportCardToursCruisesEnabled,
  ),
  (state: App.State) => state.cruise.cruiseOfferSnapshot,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => state.experience.experiences,
  (
    order,
    regionCode,
    spoofed,
    isToursCruisesFeatureEnabled,
    cruiseOfferSnapshots,
    offers,
    flightDetails,
    experienceOffers,
  ): boolean => {
    if (!order) return false

    // Countries come from the items a policy would cover, so a cancelled item on the
    // order neither stands in for the trip's destinations nor blocks resolving them.
    const insurableSlice = getPassportCardInsurableOrderSlice(order)
    const flightDetailsByOrderItems = getFlightDetailsByOrderItems(insurableSlice, flightDetails)
    const destinationCountries = getInsuranceDestinationCountriesFromOrder(
      insurableSlice,
      cruiseOfferSnapshots,
      offers,
      flightDetailsByOrderItems,
      experienceOffers,
    )

    return canAddPassportCardInsuranceToOrder(order, {
      regionCode,
      isSpoofed: spoofed,
      isToursCruisesFeatureEnabled,
      destinationCountries,
    })
  },
)

export default isPassportCardInsurancePostPurchaseEnabled
